import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { ISurveyFeedbackItem, ISurveyItem } from 'pages/private/satisfaction-survey-details/types';
import { IDefaultOption } from 'components/common/select';

interface ISurveysState {
  surveys: ISurveyItem[];
  launchSurveyOptions: IDefaultOption[];
  surveyResponses: ISurveyFeedbackItem[];
  selectedSurveyFeedback: ISurveyFeedbackItem | null;
  totalSurveyCount: number;
  totalSurveyResponseCount: number;
}

export interface IAddEditSurveyRequest {
  title: string;
  type?: string;
  questions: string[];
  is_draft: boolean;
  survey_id?: string;
  brand_id?: string;
}

export interface IGetSurveyFeedbackParams {
  searchString?: string;
}

interface ISurveyItemResponse {
  surveyList: ISurveyItem[];
  record_count: number;
}

interface ISurveyFeedbackItemResponse {
  responseDetails: ISurveyFeedbackItem[];
  record_count: number;
}

const initialState: ISurveysState = {
  surveys: [],
  launchSurveyOptions: [],
  surveyResponses: [],
  selectedSurveyFeedback: null,
  totalSurveyCount: 0,
  totalSurveyResponseCount: 0,
};
const surveysSlice = createSlice({
  name: 'Surveys',
  initialState: initialState,
  reducers: {
    setSurveys: (state, { payload }: { payload: ISurveyItemResponse }) => {
      state.surveys = [...payload.surveyList];
      state.totalSurveyCount = payload.record_count;
    },
    setLaunchSurveysOption: (state, { payload }: { payload: IDefaultOption[] }) => {
      state.launchSurveyOptions = payload;
    },
    setSurveyResponses: (state, { payload }: { payload: ISurveyFeedbackItemResponse }) => {
      state.surveyResponses = [...payload.responseDetails];
      state.totalSurveyResponseCount = payload.record_count;
    },
    setSelectedSurveyFeedback: (state, { payload }: { payload: ISurveyFeedbackItem | null }) => {
      state.selectedSurveyFeedback = payload;
    },
  },
});

export const getSurveysList =
  (payload: { offset?: number; limit?: number }) => async (dispatch: Dispatch) => {
    try {
      const { offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      const { data: responseData } = await api.get(`/admin/survey${queryString}`);

      dispatch(setSurveys(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const createSurvey =
  (payload: { request: IAddEditSurveyRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/survey', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateSurvey =
  (payload: { survey_id: string; request: IAddEditSurveyRequest }) =>
  async (dispatch: Dispatch) => {
    try {
      const { survey_id, request } = payload;
      const { data: responseData } = await api.post(`/admin/survey/${survey_id}`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getSurveyDetails = (payload: { survey_id: string }) => async () => {
  try {
    const { survey_id } = payload;
    const { data: responseData } = await api.get(`/admin/survey/${survey_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const removeSurvey = (payload: { survey_id: string }) => async () => {
  try {
    const { survey_id } = payload;
    const { data: responseData } = await api.delete(`/admin/survey/${survey_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const updateSurveyStatus =
  (payload: { params: { survey_id: string; activated: boolean } }) => async () => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/status/survey', { params });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getSurveyLabels = (payload: {}) => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/surveys/labels');
    dispatch(setLaunchSurveysOption(responseData.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getSurveyResponse =
  (payload: { params: IGetSurveyFeedbackParams;searchString?: string;offset: number; limit: number }) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, searchString, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''
      }`;
      const { data: responseData } = await api.get(`/admin/survey-response${queryString}`, { params });
      dispatch(setSurveyResponses(responseData.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const { setSurveys, setLaunchSurveysOption, setSurveyResponses, setSelectedSurveyFeedback } =
  surveysSlice.actions;
export default surveysSlice.reducer;
